<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { useStore } from 'vuex'
import MediaflowService from '@/services/MediaflowService'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import Alert from '@/components/Atoms/Alert.vue'
import InlineDateTimePicker from '@/components/Atoms/InlineDateTimePicker.vue'
import AddPlayoutErrorAlert from '@/components/TVPlayout/AddPlayoutErrorAlert.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconPlusSmall from '@/components/icon/IconPlusSmall.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconInfo from '@/components/icon/IconInfo.vue'
import MediaflowScheduleTypePicker from '@/components/Mediaflow/MediaflowScheduleTypePicker.vue'
import MediaflowScheduleFlowSelect from '@/components/Mediaflow/MediaflowScheduleFlowSelect.vue'
import MediaflowScheduleIntervalField from '@/components/Mediaflow/MediaflowScheduleIntervalField.vue'
import {
	emptyForm,
	emptyAd,
	emptyScene,
	fromApiSchedule,
	toApiPayload,
	validateForm,
	findOverlap,
	summarizeForm,
} from '@/components/Mediaflow/adScheduleForm'
import { scheduleLimitReached, scheduleLimitReason } from '@/components/Mediaflow/mediaflowMeta'

/**
 * Automation editor, shown inside the Automation panel: create or edit one
 * schedule that tells the server when to switch flows. Mounted fresh for each
 * edit (the panel keys it), so the form starts from the props.
 */
const props = defineProps({
	mediaflowId: { type: String, required: true },
	/** Edit this schedule; null = create. */
	schedule: { type: Object, default: null },
	/** Create pre-filled for a break: `{ flow, returnFlowId }`. */
	breakPrefill: { type: Object, default: null },
})

const emit = defineEmits(['saved', 'cancel'])

const store = useStore()
const { notify } = useNotification()

const isEditMode = ref(false)
const editingScheduleId = ref(null)
const processing = ref(false)
const error = ref(null)
const existingSchedules = ref([])
const overlap = ref(null)
const nameInput = ref(null)

const form = ref(emptyForm())
const touched = reactive({})
const attempted = ref(false)

const mediaflow = computed(() => store.getters['Mediaflows/mediaflow'])
const flows = computed(() => mediaflow.value?.flows || [])
const flowName = (id) => flows.value.find((flow) => String(flow._id) === String(id))?.name || ''

// Schedules can't be set in the past; the picker disables earlier slots.
const minDate = computed(() => new Date())

const maxSchedules = computed(() => Number(store.getters['User/maxMediaflowAdSchedule']) || 0)
/** Creating would go over the plan's schedule limit (editing never does). */
const limitReason = computed(() => (!isEditMode.value && scheduleLimitReached(existingSchedules.value.length, maxSchedules.value)
	? scheduleLimitReason(existingSchedules.value.length, maxSchedules.value)
	: ''))

const errors = computed(() => validateForm(form.value))
const isValid = computed(() => Object.keys(errors.value).length === 0)
const summary = computed(() => summarizeForm(form.value, flowName))

const showsWindow = computed(() => ['recurring', 'time-slot-block'].includes(form.value.type))
const showsStart = computed(() => ['single-time', 'multi-ad-block'].includes(form.value.type))
const showsInterval = computed(() => ['recurring', 'scene-rotation', 'time-slot-block'].includes(form.value.type))
const showsFlow = computed(() => ['single-time', 'recurring'].includes(form.value.type))
const showsDuration = computed(() => form.value.type && form.value.type !== 'multi-ad-block')

const intervalLabel = computed(() => (form.value.type === 'scene-rotation' ? 'Rotate every' : form.value.type === 'time-slot-block' ? 'Play every' : 'Repeat every'))
const durationLabel = computed(() => (form.value.type === 'scene-rotation' ? 'Each scene plays for (seconds)' : form.value.type === 'time-slot-block' ? 'Slot length (seconds)' : 'Play for (seconds)'))

/** Inline messages appear once a field was touched, or after a save attempt. */
const fieldError = (key) => ((touched[key] || attempted.value) ? errors.value[key] || '' : '')
const touch = (key) => { touched[key] = true }
// Pickers emit null while the operator is still choosing; only a real value counts as touched.
const touchWhenSet = (key, value) => { if (value !== null && value !== undefined && value !== '') touched[key] = true }

// ---------------------------------------------------------------- open / close

const resetForm = () => {
	form.value = emptyForm()
	Object.keys(touched).forEach((key) => { delete touched[key] })
	attempted.value = false
	error.value = null
	overlap.value = null
	processing.value = false
}

const loadExistingSchedules = async () => {
	if (!props.mediaflowId) return
	try {
		const schedules = await MediaflowService.getAdSchedules(props.mediaflowId)
		existingSchedules.value = Array.isArray(schedules) ? schedules : []
	} catch (err) {
		console.error('Failed to fetch existing schedules', err)
		existingSchedules.value = []
	}
}

const cancel = () => emit('cancel')

// ---------------------------------------------------------------- rows

const addAd = () => { form.value.ads.push(emptyAd()) }
const removeAd = (id) => { form.value.ads = form.value.ads.filter((ad) => ad.id !== id) }
const addScene = () => { form.value.scenes.push(emptyScene()) }
const removeScene = (id) => { form.value.scenes = form.value.scenes.filter((scene) => scene.id !== id) }

// ---------------------------------------------------------------- overlaps

let overlapTimer = null
watch(form, () => {
	clearTimeout(overlapTimer)
	overlapTimer = setTimeout(() => {
		overlap.value = findOverlap(form.value, existingSchedules.value, isEditMode.value ? editingScheduleId.value : null)
	}, 300)
}, { deep: true })
watch(existingSchedules, () => {
	overlap.value = findOverlap(form.value, existingSchedules.value, isEditMode.value ? editingScheduleId.value : null)
})
onBeforeUnmount(() => clearTimeout(overlapTimer))

// ---------------------------------------------------------------- save

const save = async () => {
	if (processing.value) return
	attempted.value = true
	if (!isValid.value) return
	if (!isEditMode.value) await loadExistingSchedules()
	if (limitReason.value) { error.value = limitReason.value; return }

	processing.value = true
	error.value = null
	try {
		const payload = toApiPayload(form.value)
		if (isEditMode.value && editingScheduleId.value) {
			await MediaflowService.updateAdSchedule(props.mediaflowId, editingScheduleId.value, payload)
			notify({ group: 'success', text: 'Schedule updated' })
		} else {
			await MediaflowService.createAdSchedule(props.mediaflowId, payload)
			notify({ group: 'success', text: 'Schedule created' })
		}
		emit('saved')
	} catch (err) {
		console.error('Failed to save schedule', err)
		error.value = err?.message || 'Failed to save schedule'
	} finally {
		processing.value = false
	}
}

// ---------------------------------------------------------------- start

if (props.schedule) {
	isEditMode.value = true
	editingScheduleId.value = props.schedule._id || null
	form.value = fromApiSchedule(props.schedule)
} else if (props.breakPrefill?.flow) {
	// "Switch to this break once, at a time I pick, for its duration, then return where it says."
	const { flow, returnFlowId = null } = props.breakPrefill
	const settings = flow.break || {}
	form.value = {
		...emptyForm(),
		name: flow.name ? `${flow.name} schedule` : '',
		type: 'single-time',
		flowId: flow._id || null,
		duration: String(settings.durationSec || 30),
		returnFlowId: settings.returnFlowId || returnFlowId || null,
	}
}
onMounted(async () => {
	void loadExistingSchedules()
	await nextTick()
	if (!isEditMode.value) nameInput.value?.focus?.()
})

defineExpose({ isEditMode })
</script>

<template>
	<form class="flex flex-col gap-5" novalidate data-testid="schedule-editor" @submit.prevent="save">

			<AddPlayoutErrorAlert v-if="error" :error="error" />
			<Alert v-else-if="limitReason" type="warning" class="!mb-0" data-testid="schedule-limit">{{ limitReason }}</Alert>

			<FormInput
				id="schedule-name"
				ref="nameInput"
				v-model="form.name"
				label="Name"
				placeholder="e.g. Top-of-hour ad break"
				:maxlength="120"
				:is-error="!!fieldError('name')"
				:helper="fieldError('name') || undefined"
				data-testid="schedule-name"
				@blur="touch('name')"
			/>

			<div>
				<span class="mb-2 block text-tiny font-medium text-surface-text-1">Type</span>
				<MediaflowScheduleTypePicker v-model="form.type" />
				<p v-if="fieldError('type')" class="mt-1 text-xs text-error">{{ fieldError('type') }}</p>
			</div>

			<template v-if="form.type">
				<!-- When -->
				<section class="flex flex-col gap-3" aria-labelledby="schedule-when">
					<h3 id="schedule-when" class="m-0 text-[0.65rem] font-semibold uppercase tracking-wider text-surface-6">When</h3>

					<div v-if="showsStart">
						<label class="mb-2 block text-tiny font-medium text-surface-text-1">Date &amp; time</label>
						<div data-testid="schedule-start">
							<InlineDateTimePicker v-model="form.startTime" size="sm" open-on-click inline-on-mobile :min-date="minDate" time-label="Start time" @update:model-value="touchWhenSet('startTime', $event)" />
						</div>
						<p v-if="fieldError('startTime')" class="mt-1 text-xs text-error">{{ fieldError('startTime') }}</p>
						<p v-else class="mt-1 text-xs text-surface-8">Your local time.</p>
					</div>

					<div v-if="showsWindow" class="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<div class="min-w-0">
							<label class="mb-2 block text-tiny font-medium text-surface-text-1">From</label>
							<div data-testid="schedule-start">
								<InlineDateTimePicker v-model="form.startTime" size="sm" open-on-click inline-on-mobile :min-date="minDate" time-label="Start time" @update:model-value="touchWhenSet('startTime', $event)" />
							</div>
							<p v-if="fieldError('startTime')" class="mt-1 text-xs text-error">{{ fieldError('startTime') }}</p>
						</div>
						<div class="min-w-0">
							<label class="mb-2 block text-tiny font-medium text-surface-text-1">Until</label>
							<div data-testid="schedule-end">
								<InlineDateTimePicker v-model="form.endTime" size="sm" open-on-click inline-on-mobile align="right" :min-date="form.startTime || minDate" time-label="End time" @update:model-value="touchWhenSet('endTime', $event)" />
							</div>
							<p v-if="fieldError('endTime')" class="mt-1 text-xs text-error">{{ fieldError('endTime') }}</p>
						</div>
						<p class="col-span-full m-0 text-xs text-surface-8">Your local time. Nothing runs outside this window.</p>
					</div>

					<MediaflowScheduleIntervalField
						v-if="showsInterval"
						v-model:interval="form.interval"
						v-model:custom-value="form.customValue"
						v-model:custom-unit="form.customUnit"
						:label="intervalLabel"
						:error="fieldError('interval')"
						:custom-error="fieldError('customValue')"
						@update:interval="touch('interval')"
						@blur="touch"
					/>
				</section>

				<!-- What plays -->
				<section class="flex flex-col gap-3" aria-labelledby="schedule-what">
					<h3 id="schedule-what" class="m-0 text-[0.65rem] font-semibold uppercase tracking-wider text-surface-6">What plays</h3>

					<MediaflowScheduleFlowSelect
						v-if="showsFlow"
						v-model="form.flowId"
						:flows="flows"
						label="Switch to"
						placeholder="Select a scene"
						:error="fieldError('flowId')"
						testid="schedule-flow"
						@update:model-value="touch('flowId')"
					/>

					<div v-if="showsDuration" data-testid="schedule-duration">
						<FormInput
							v-model="form.duration"
							:label="durationLabel"
							type="number"
							min="1"
							placeholder="30"
							:is-error="!!fieldError('duration')"
							:helper="fieldError('duration') || undefined"
							@blur="touch('duration')"
						/>
					</div>

					<!-- Ad block: a sequence of flows played back to back -->
					<div v-if="form.type === 'multi-ad-block'" class="flex flex-col gap-2">
						<div class="flex items-center justify-between">
							<span class="text-tiny font-medium text-surface-text-1">Ad sequence</span>
							<FormButton type="secondary" size="sm" @click="addAd">
								<icon-base :width="12" :height="12" :width-view-box="14" :height-view-box="14"><icon-plus-small /></icon-base>
								<span class="ml-1.5">Add ad</span>
							</FormButton>
						</div>
						<div v-for="(ad, index) in form.ads" :key="ad.id" class="flex flex-col gap-3 rounded-lg bg-surface-3 p-3">
							<div class="flex items-center justify-between">
								<span class="text-xs font-medium text-surface-8">Ad {{ index + 1 }}</span>
								<FormButton v-if="form.ads.length > 1" is-icon type="danger" size="sm" :aria-label="`Remove ad ${index + 1}`" @click="removeAd(ad.id)">
									<icon-base><icon-bin /></icon-base>
								</FormButton>
							</div>
							<MediaflowScheduleFlowSelect v-model="ad.flowId" :flows="flows" label="Scene" placeholder="Select a scene" @update:model-value="touch('ads')" />
							<FormInput v-model="ad.duration" label="Play for (seconds)" type="number" min="1" placeholder="30" @blur="touch('ads')" />
						</div>
						<p v-if="fieldError('ads')" class="m-0 text-xs text-error">{{ fieldError('ads') }}</p>
						<p v-else class="m-0 text-xs text-surface-8">Ads play in order, then it returns to the scene below.</p>
					</div>

					<!-- Flow rotation: named scenes cycled in order -->
					<div v-if="form.type === 'scene-rotation'" class="flex flex-col gap-2">
						<div class="flex items-center justify-between">
							<span class="text-tiny font-medium text-surface-text-1">Scenes</span>
							<FormButton type="secondary" size="sm" @click="addScene">
								<icon-base :width="12" :height="12" :width-view-box="14" :height-view-box="14"><icon-plus-small /></icon-base>
								<span class="ml-1.5">Add scene</span>
							</FormButton>
						</div>
						<div v-for="(scene, index) in form.scenes" :key="scene.id" class="flex items-end gap-2">
							<FormInput v-model="scene.name" :label="`Scene ${index + 1}`" placeholder="Scene name" class="min-w-0 flex-1" @blur="touch('scenes')" />
							<FormButton v-if="form.scenes.length > 1" is-icon type="danger" size="md" class="shrink-0" :aria-label="`Remove scene ${index + 1}`" @click="removeScene(scene.id)">
								<icon-base><icon-bin /></icon-base>
							</FormButton>
						</div>
						<p v-if="fieldError('scenes')" class="m-0 text-xs text-error">{{ fieldError('scenes') }}</p>
						<p v-else class="m-0 text-xs text-surface-8">Scenes play in order, then loop.</p>
					</div>
				</section>

				<!-- Return to -->
				<section class="flex flex-col gap-3" aria-labelledby="schedule-return">
					<h3 id="schedule-return" class="m-0 text-[0.65rem] font-semibold uppercase tracking-wider text-surface-6">Return to</h3>
					<MediaflowScheduleFlowSelect
						v-model="form.returnFlowId"
						:flows="flows"
						label="Back on air afterwards"
						placeholder="Select a scene"
						:error="fieldError('returnFlowId')"
						testid="schedule-return-flow"
						position="top-left"
						@update:model-value="touch('returnFlowId')"
					/>
				</section>

				<Alert v-if="overlap" type="warning2" role="status" data-testid="schedule-overlap">
					Overlaps with “{{ overlap.name }}”, which is also active.
				</Alert>
			</template>

			<!-- Actions stay in view at the bottom of the panel -->
			<div class="sticky bottom-0 -mx-4 mt-1 flex flex-col gap-3 bg-surface-2 px-4 pb-4 pt-3 shadow-[0_-12px_16px_-12px_rgba(0,0,0,0.35)] sm:-mx-6 sm:px-6">
				<p v-if="summary" class="m-0 flex items-start gap-2 rounded-lg bg-surface-3 px-3 py-2 text-xs leading-snug text-surface-8" data-testid="schedule-summary" aria-live="polite">
					<icon-base class="mt-px shrink-0 text-surface-7"><icon-info /></icon-base>
					<span>{{ summary }}</span>
				</p>
				<p v-if="attempted && !isValid" class="m-0 text-xs text-error">Fill in the highlighted fields to continue.</p>
				<div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
					<FormButton type="secondary" size="md" class="w-full justify-center sm:w-auto" :disabled="processing" data-testid="schedule-cancel" @click="cancel">Cancel</FormButton>
					<FormButton type="main" size="md" class="w-full justify-center sm:w-auto" :disabled="processing || !!limitReason" :is-loading="processing" data-testid="schedule-submit" @click="save">
						{{ isEditMode ? 'Save changes' : 'Create schedule' }}
					</FormButton>
				</div>
			</div>
	</form>
</template>
